import { Button } from "@fp/ui/components/base/buttons/button";
import { Input } from "@fp/ui/components/base/input/input";
import { Select, SelectItem } from "@fp/ui/components/base/select/select";
import {
	PageContentContainer,
	PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { useGetRoles } from "@fp/admin-api-client/roles/roles.client";
import { useCreateInvitation } from "@fp/admin-api-client/sent-invitations/sent-invitations.client";
import { InvitationType } from "@fp/admin-api-client/types/invitationType";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { useState, useEffect } from "react";
import { getErrorMessage, type ApiErrorResponse } from "@/lib/axios";
import { toast } from "@/lib/toast";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STAFF_ROLES = ["ORG_ADMIN", "ORG_MANAGER", "ORG_PAYOPS"];

export const Route = createFileRoute("/invitations/new")({
	component: RouteComponent,
	pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
	const navigate = useNavigate();

	const [email, setEmail] = useState("");
	const [roleCode, setRoleCode] = useState("");

	const { data: rolesResponse, isLoading: isLoadingRoles } = useGetRoles();

	const staffRoles =
		rolesResponse?.data.filter((role) => STAFF_ROLES.includes(role.code)) ?? [];

	useEffect(() => {
		if (staffRoles.length > 0 && (!roleCode || !STAFF_ROLES.includes(roleCode))) {
			setRoleCode(staffRoles[0].code);
		}
	}, [staffRoles, roleCode]);

	const createMutation = useCreateInvitation({
		mutation: {
			onSuccess: () => {
				toast.success("Invitation sent!");
				navigate({ to: "/invitations" });
			},
			onError: (err) => {
				if (
					isAxiosError<ApiErrorResponse>(err) &&
					err.response?.status === 409 &&
					err.response.data?.code === "ALREADY_MEMBER" &&
					err.response.data.message
				) {
					toast.warning(err.response.data.message);
				} else {
					toast.error(getErrorMessage(err, "Failed to send invitation. Please try again."));
				}
			},
		},
	});

	const trimmedEmail = email.trim();
	const isEmailValid = EMAIL_PATTERN.test(trimmedEmail);

	const handleSubmit = () => {
		createMutation.mutate({
			data: {
				email: trimmedEmail,
				roleCode,
				invitationType: InvitationType.Staff,
			},
		});
	};

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Invite a Staff Member"
				parent={{ url: "/invitations", label: "Invitations" }}
			/>
			<PageContentContainer className="space-y-6">
				<div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
					<Input
						label="Email Address"
						type="email"
						value={email}
						onChange={setEmail}
						isRequired
						autoFocus
					/>
					{trimmedEmail !== "" && !isEmailValid && (
						<p className="-mt-2 text-sm text-error-primary" role="alert">
							Enter a valid email address
						</p>
					)}

					<div>
						<div className="block text-sm font-medium text-gray-700 mb-1">
							Role
						</div>
						{isLoadingRoles ? (
							<div className="text-sm text-gray-500">Loading roles...</div>
						) : (
							<Select
								selectedKey={roleCode}
								onSelectionChange={(key: React.Key | null) => setRoleCode(key as string)}
							>
								{staffRoles.map((role) => (
									<SelectItem key={role.code} id={role.code}>
										{role.name}
									</SelectItem>
								))}
							</Select>
						)}
						<p className="mt-1 text-sm text-gray-500">
							Staff members help manage the organization. Add talent members and companies you pay from the roster.
						</p>
					</div>

					<Button
						color="primary"
						onClick={handleSubmit}
						isLoading={createMutation.isPending}
						isDisabled={
							createMutation.isPending ||
							!isEmailValid ||
							!roleCode ||
							isLoadingRoles ||
							staffRoles.length === 0
						}
						className="w-full"
					>
						Send Invitation
					</Button>
				</div>
			</PageContentContainer>
		</AdminLayout>
	);
}
